import { get, startCase, filter, capitalize, each, find } from 'lodash';
import { formatCurrency, formatCurrencyRounded } from 'utils';
import moment from 'moment-timezone';
import { getCleanBundleItems } from 'utils/order';
import { getNextBillingDate } from 'utils/date';
import ObjectID from 'bson-objectid';
import currency from 'currency.js';

export const SUBSCRIPTION_INTERVAL_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'daily', label: 'Daily' },
];

export const DEFAULT_SUBSCRIPTION_PLAN = {
  name: SUBSCRIPTION_INTERVAL_OPTIONS[0].label,
  description: '',
  price: '',
  billing_schedule: {
    interval: SUBSCRIPTION_INTERVAL_OPTIONS[0].value,
    interval_count: 1,
    limit: '',
    trial_days: 0,
  },
  has_order_schedule: false,
  order_schedule: {
    interval: SUBSCRIPTION_INTERVAL_OPTIONS[0].value,
    interval_count: 1,
    limit: '',
  },
};

export function getDeprecatedSubscriptionOptionPlan(product, values) {
  if (values.options) {
    let subOptionValue;

    each(product.options, (op) => {
      for (const valueOp of values.options) {
        if (valueOp.id === op.id && op.subscription === true) {
          for (const opValue of op.values || []) {
            if (valueOp.value === opValue.id) {
              subOptionValue = opValue;
              break;
            }
          }
          break;
        }
      }
    });

    if (subOptionValue) {
      return {
        billing_schedule: {
          interval: subOptionValue.subscription_interval,
          interval_count: subOptionValue.subscription_interval_count,
          trial_days: subOptionValue.subscription_trial_days,
        },
      };
    }
  }

  return null;
}

/**
 * The product's plan a saved purchase option is on: 'custom' when its schedule
 * was changed from every plan's, or null when it isn't a subscription
 */
export function getPurchaseOptionPlanId(product, purchaseOption) {
  const subscriptionPlans =
    get(product, 'purchase_options.subscription.plans') || [];

  const editPlan = purchaseOption
    ? find(subscriptionPlans, (plan) => {
        const matched =
          plan.id === purchaseOption.plan_id &&
          get(plan, 'billing_schedule.interval') ===
            get(purchaseOption, 'billing_schedule.interval') &&
          get(plan, 'billing_schedule.interval_count') ===
            get(purchaseOption, 'billing_schedule.interval_count') &&
          (get(plan, 'billing_schedule.trial_days') || null) ===
            (get(purchaseOption, 'billing_schedule.trial_days') || null) &&
          (get(plan, 'billing_schedule.limit') || null) ===
            (get(purchaseOption, 'billing_schedule.limit') || null);

        return matched;
      })
    : null;

  const isSubscription =
    purchaseOption &&
    Object.keys(purchaseOption).length > 0 &&
    (purchaseOption.type === 'subscription' ||
      Object.keys(purchaseOption.billing_schedule || {}).length > 0);

  return editPlan
    ? editPlan.id
    : isSubscription && subscriptionPlans.length > 0
    ? 'custom'
    : null;
}

export function subscriptionIntervalOptions(intervalCount) {
  return intervalCount > 1
    ? [
        { value: 'monthly', label: 'Months' },
        { value: 'yearly', label: 'Years' },
        { value: 'weekly', label: 'Weeks' },
        { value: 'daily', label: 'Days' },
      ]
    : [
        { value: 'monthly', label: 'Month' },
        { value: 'yearly', label: 'Year' },
        { value: 'weekly', label: 'Week' },
        { value: 'daily', label: 'Day' },
      ];
}

// Get subscription status name
export function subscriptionStatus(sub) {
  return startCase(sub.status) || 'Unknown';
}

export function subscriptionTitle(sub) {
  return sub && sub.product
    ? sub.product.name
    : sub && sub.product_name
    ? sub.product_name
    : '(Unknown subscription)';
}

// Get a nice description of recurring subscription interval
export function subscriptionIntervalDesc(sub, params = {}) {
  const { interval, interval_count, trial_days, limit, limit_current } = get(
    sub,
    'billing_schedule',
    sub,
  );
  const plan_description = get(sub, 'plan_description');
  let desc = interval || 'monthly';

  if (plan_description) {
    desc = plan_description;
  } else if (interval_count > 1) {
    switch (interval) {
      case 'daily':
        desc = `Every ${interval_count} days`;
        break;
      case 'weekly':
        desc = `Every ${interval_count} weeks`;
        break;
      case 'monthly':
        desc = `Every ${interval_count} months`;
        break;
      case 'yearly':
        desc = `Every ${interval_count} years`;
        break;
      default:
        return '';
    }
  } else {
    if (params.every) {
      switch (interval) {
        case 'daily':
          desc = 'day';
          break;
        case 'weekly':
          desc = 'week';
          break;
        case 'monthly':
          desc = 'month';
          break;
        case 'yearly':
          desc = 'year';
          break;
        default:
          return '';
      }
      desc = `Every ${desc}`;
    }
  }

  if (limit > 0) {
    desc = `${desc} (${
      limit_current && limit_current < limit
        ? `${limit_current} of ${limit}`
        : `limit ${limit}`
    })`;
  }

  const orderSchedule = get(sub, 'order_schedule', {});
  if (sub.order_schedule && orderSchedule.interval) {
    if (orderSchedule.interval_count > 1) {
      switch (orderSchedule.interval) {
        case 'daily':
          desc += `, orders every ${orderSchedule.interval_count} days`;
          break;
        case 'weekly':
          desc += `, orders every ${orderSchedule.interval_count} weeks`;
          break;
        case 'monthly':
          desc += `, orders every ${orderSchedule.interval_count} months`;
          break;
        case 'yearly':
          desc += `, orders every ${orderSchedule.interval_count} years`;
          break;
        default:
          return '';
      }
    } else {
      switch (orderSchedule.interval) {
        case 'daily':
          desc += ', orders daily';
          break;
        case 'weekly':
          desc += ', orders weekly';
          break;
        case 'monthly':
          desc += ', orders monthly';
          break;
        case 'yearly':
          desc += ', orders yearly';
          break;
        default:
          return '';
      }
    }

    if (orderSchedule.limit > 0) {
      desc = `${desc} (${
        orderSchedule.limit_current &&
        orderSchedule.limit_current < orderSchedule.limit
          ? `${orderSchedule.limit_current} of ${orderSchedule.limit}`
          : `limit ${orderSchedule.limit}`
      })`;
    }
  }

  if (params.currency && sub.price > 0) {
    desc = `${desc}, ${formatCurrencyRounded(sub.price, params.currency)}`;
  }

  const hasTrial =
    (sub.trial === true || sub.trial === undefined) && trial_days > 0;
  if (hasTrial) {
    desc = `${desc}, ${trial_days}-day trial`;
  }

  return capitalize(desc);
}

/*
  Similar to subscriptionIntervalDesc but description value will be used
  for displaying in report table.
*/
export function subscriptionReportPlanDescription(
  subscriptionReport,
  currency,
) {
  const {
    billing_schedule: { interval_count, interval },
    price,
  } = subscriptionReport;

  let intervalStr = null;
  const recordCurrency = subscriptionReport.currency || currency;
  if (interval_count === 1) {
    intervalStr = interval[0].toUpperCase() + interval.slice(1);
    return `${intervalStr}, ${formatCurrency(price, recordCurrency)}`;
  } else {
    switch (interval) {
      case 'daily':
        intervalStr = 'days';
        break;
      case 'weekly':
        intervalStr = 'weeks';
        break;
      case 'monthly':
        intervalStr = 'months';
        break;
      case 'yearly':
        intervalStr = 'years';
        break;
      default:
        break;
    }

    return `Every ${interval_count} ${intervalStr}, ${formatCurrency(
      price,
      recordCurrency,
    )}`;
  }
}

export function subscriptionOptionValueNameDeprecated(optionValue, params) {
  return subscriptionIntervalDesc(
    {
      interval: optionValue.subscription_interval,
      interval_count: optionValue.subscription_interval_count,
      trial_days: optionValue.subscription_trial_days,
      price: optionValue.price,
    },
    params,
  );
}

export function subscriptionPlanDesc(plan, params) {
  return subscriptionIntervalDesc(plan, params);
}

// Get a nice description of recurring subscription total
export function subscriptionPriceDesc(sub) {
  let extra = '';
  const recurring_total = currency(sub.recurring_total, { precision: 2 }).value;
  if (sub.grand_total > recurring_total) {
    extra = `+ ${formatCurrencyRounded(
      sub.grand_total - recurring_total,
      sub.currency,
    )}`;
  }

  return `${formatCurrencyRounded(
    recurring_total,
    sub.currency,
  )} / ${subscriptionIntervalDesc(sub)} ${extra}`;
}

function subscriptionItemHasOrderDelivery(item) {
  return ['shipment', 'giftcard'].indexOf(item.delivery) >= 0;
}

function subscriptionProductHasOrderItems(product) {
  // TODO: remove deprecated subscription delivery
  if (
    product &&
    product.delivery === 'subscription' &&
    product.bundle_items &&
    product.bundle_items.length > 0
  ) {
    return true;
  } else if (product.bundle_items && product.bundle_items.length > 0) {
    return (
      product.bundle_items.filter(subscriptionItemHasOrderDelivery).length > 0
    );
  } else if (subscriptionItemHasOrderDelivery(product)) {
    return true;
  }
  return false;
}

export function subscriptionHasOrderItems(record, product) {
  return (
    subscriptionProductHasOrderItems(product) ||
    filter(record.items, subscriptionItemHasOrderDelivery).length > 0
  );
}

function getCurrencies(context) {
  const { client, account } = context;
  let { pricedCurrencies } = client;
  if (!account.hasPricedCurrencies) {
    pricedCurrencies = [];
  }

  const subscriptionDefaultCurrencies = pricedCurrencies.reduce(
    (prev, { active, code }) => {
      if (active) {
        prev.$currency[code] = { price: '' };
      }

      return prev;
    },
    { $currency: {} },
  );

  return pricedCurrencies.length > 0 ? subscriptionDefaultCurrencies : {};
}

function getLocales(context) {
  const { client } = context;
  const { locales } = client;

  const subscriptionDefaultLocales = locales.reduce(
    (prev, { active, code }) => {
      if (active) {
        prev.$locale[code] = { description: '' };
      }

      return prev;
    },
    { $locale: {} },
  );

  return locales.length > 0 ? subscriptionDefaultLocales : {};
}

export function getDefaultSubscriptionPlan(context, withDefaultPlan = true) {
  const defaultSubscriptionPlan = {
    ...getCurrencies(context),
    ...getLocales(context),
    ...(withDefaultPlan ? {
      ...DEFAULT_SUBSCRIPTION_PLAN,
      id: ObjectID().toHexString(),
      active: true,
    }: {}),
  };

  return defaultSubscriptionPlan;
}

/**
 * The PUT classic's Edit plan sends from its item form's values (product,
 * variant_id, options, quantity, set_price, purchase_option, prorated):
 * the plan's schedules replace the old ones, options are set whole.
 */
export function getSubscriptionPlanUpdate(values) {
  return {
    billing_schedule: '',
    order_schedule: '',
    ...values,
    ...values.purchase_option,
    options: undefined,
    product: undefined,
    variant: undefined,
    set_price: undefined,
    purchase_option: undefined,
    price: values.set_price,
    product_id: values.product.id,
    $set: {
      options: values.options,
    },
  };
}

/** An invoice item as classic's Edit invoice items saves it. */
export function makeEditedSubscriptionItem(item) {
  return {
    id: item.id,
    product: undefined,
    product_id: item.product && item.product.id,
    variant: undefined,
    variant_id: item.variant && item.variant.id,
    price: item.set_price,
    quantity: item.quantity,
    options: item.options,
    removed: item.removed,
    description: item.description,
    recurring: item.recurring,
    bundle_items: getCleanBundleItems(item.bundle_items),
  };
}

/** Classic's label for the next order date: first, next, or the last one. */
export function getOrderPeriodLabel(record) {
  const hasSeparateSchedule = Boolean(record.order_schedule?.interval);

  const schedule = hasSeparateSchedule
    ? record.order_schedule
    : record.billing_schedule;

  const order_period_end = hasSeparateSchedule
    ? record.date_order_period_end
    : record.date_period_end;

  if (
    order_period_end &&
    schedule?.limit > 0 &&
    schedule.limit <= schedule.limit_current
  ) {
    return 'Order period end';
  }

  return `${record.orders?.count > 0 ? 'Next' : 'First'} order`;
}

/** Classic's label for the next invoice date: first, next, or the last one. */
export function getBillingPeriodLabel(record) {
  const { billing_schedule } = record;

  if (
    record.date_period_end &&
    billing_schedule?.limit > 0 &&
    billing_schedule.limit <= billing_schedule.limit_current
  ) {
    return 'Invoice period end';
  }

  return `${record.invoices?.count > 0 ? 'Next' : 'First'} invoice`;
}

/** When the next order is created: its own schedule's period, or billing's. */
export function getNextOrderDate(record) {
  if (!record.order_schedule?.interval) {
    return record.date_period_end;
  }
  return new Date(record.date_order_period_start) > new Date()
    ? record.date_order_period_start
    : record.date_order_period_end;
}

/**
 * Classic PauseSubscription: when a subscription paused at the end of its
 * billing ('billing') or order ('order') cycle resumes after skipping cycles.
 */
export function getPauseResumeDate(subscription, schedule, skippedCycles) {
  let { interval, interval_count } = subscription;
  let datePeriodEnd =
    subscription.date_period_end || subscription.date_trial_end;

  if (schedule === 'order' && subscription.order_schedule) {
    ({ interval, interval_count } = subscription.order_schedule);
    datePeriodEnd = subscription.date_order_period_end;
  }

  return moment(
    getNextBillingDate(interval, datePeriodEnd, interval_count * skippedCycles),
  ).utc();
}

/**
 * The cycle ends a pause or cancel can wait for, soonest first, as classic's
 * pause and cancel dialogs order them: billing, and order when orders run on
 * their own schedule. `distinct` leaves out an order end at the billing end.
 */
export function getSubscriptionCycleEnds(subscription, { distinct } = {}) {
  const billing = {
    schedule: 'billing',
    date: subscription.date_period_end || subscription.date_trial_end,
  };
  const separate = Boolean(
    subscription.order_schedule?.interval || subscription.ordering,
  );
  const orderEnd = subscription.date_order_period_end;
  if (
    !separate ||
    !orderEnd ||
    (distinct && moment(orderEnd).isSame(billing.date, 'minute'))
  ) {
    return [billing];
  }
  const order = { schedule: 'order', date: orderEnd };
  return new Date(orderEnd) > new Date(billing.date)
    ? [billing, order]
    : [order, billing];
}
